import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import {
  BLOG_CATALOG,
  BLOGS_PER_PAGE,
  getBlogImageForNumber,
} from "../constants/blogs";
import { getCategoryAssets } from "../utils/categoryAssets";
import { buildBreadcrumbListJsonLd } from "../utils/breadcrumbSchema";
import "./Blogs.css";

const Blogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const currentPage = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;

  const { bannerSrc, gallery } = useMemo(() => getCategoryAssets("blogs"), []);

  const totalPages = Math.max(1, Math.ceil(BLOG_CATALOG.length / BLOGS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);

  const pagePosts = useMemo(() => {
    const start = (safePage - 1) * BLOGS_PER_PAGE;
    return BLOG_CATALOG.slice(start, start + BLOGS_PER_PAGE).map((post) => ({
      ...post,
      image: getBlogImageForNumber(post.number, gallery),
    }));
  }, [safePage, gallery]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setSearchParams(page === 1 ? {} : { page: String(page) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const breadcrumbLd = buildBreadcrumbListJsonLd([
    { name: "Home", path: "/" },
    { name: "Blogs", path: "/blogs" },
  ]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const add = (n) => pages.push({ type: "page", n });
    const addEllipsis = (key) => pages.push({ type: "ellipsis", key });

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) add(i);
      return pages;
    }
    add(1);
    if (safePage > 3) addEllipsis("start");
    for (
      let i = Math.max(2, safePage - 1);
      i <= Math.min(totalPages - 1, safePage + 1);
      i += 1
    ) {
      add(i);
    }
    if (safePage < totalPages - 2) addEllipsis("end");
    add(totalPages);
    return pages;
  }, [safePage, totalPages]);

  return (
    <div className="blogs-page">
      <JsonLd data={breadcrumbLd} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blogs" }]} />
      <PageBanner
        title="Blogs"
        subtitle="Ideas, checklists, and perspectives from the Panthi Events team"
        image={bannerSrc}
        imageAlt="Event planning ideas and tips — Panthi Events Gujarat India"
        imageWidth={1920}
        imageHeight={1080}
      />

      <section className="section blogs-section">
        <div className="section-container blogs-section-inner">
          <div className="blogs-list-header">
            <p className="blogs-list-count">
              Showing {(safePage - 1) * BLOGS_PER_PAGE + 1}–
              {Math.min(safePage * BLOGS_PER_PAGE, BLOG_CATALOG.length)} of{" "}
              {BLOG_CATALOG.length} articles
            </p>
          </div>

          <ul className="blogs-card-grid">
            {pagePosts.map((post) => (
              <li key={post.id} className="blogs-card-grid-item">
                <Link to={`/blogs/${post.id}`} className="blogs-box-card">
                  <div className="blogs-box-card-media">
                    {post.image ? (
                      <img
                        src={post.image.src}
                        alt={post.image.alt || post.title}
                        className="blogs-box-card-img"
                        width={640}
                        height={360}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div
                        className="blogs-box-card-placeholder"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="blogs-box-card-body">
                    <div className="blogs-card-meta">
                      <span className="blogs-card-category">
                        {post.category}
                      </span>
                      <span className="blogs-card-read">{post.readTime}</span>
                    </div>
                    <h2 className="blogs-box-card-title">{post.title}</h2>
                    <p className="blogs-box-card-excerpt">{post.excerpt}</p>
                    <span className="blogs-box-card-cta">Read article →</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {totalPages > 1 ? (
            <nav className="blogs-pagination" aria-label="Blog pages">
              <button
                type="button"
                className="blogs-pagination-btn"
                disabled={safePage <= 1}
                onClick={() => goToPage(safePage - 1)}
              >
                Previous
              </button>
              <ul className="blogs-pagination-pages">
                {pageNumbers.map((item) =>
                  item.type === "ellipsis" ? (
                    <li key={item.key} className="blogs-pagination-ellipsis">
                      …
                    </li>
                  ) : (
                    <li key={item.n}>
                      <button
                        type="button"
                        className={`blogs-pagination-page${
                          item.n === safePage ? " is-active" : ""
                        }`}
                        aria-current={item.n === safePage ? "page" : undefined}
                        onClick={() => goToPage(item.n)}
                      >
                        {item.n}
                      </button>
                    </li>
                  ),
                )}
              </ul>
              <button
                type="button"
                className="blogs-pagination-btn"
                disabled={safePage >= totalPages}
                onClick={() => goToPage(safePage + 1)}
              >
                Next
              </button>
            </nav>
          ) : null}

          <div className="blogs-cta">
            <h3 className="blogs-cta-title">Have a topic you want covered?</h3>
            <p className="blogs-cta-text">
              We love hearing what clients and partners are curious about—reach
              out and we may feature your question in a future post.
            </p>
            <Link to="/contact" className="blogs-cta-btn">
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blogs;
